import {
  Heading,
  UserCompanySelector,
  UserCompanySelectorOption,
} from '@assembly-js/design-system';
import { createSelector } from '@reduxjs/toolkit';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorMessage } from 'src/components/ErrorMessage';
import { useAppSelector } from 'src/hooks/useStore';
import { useGetPortalConfigQuery } from 'src/services/api';
import { RootState } from 'src/store';
import {
  selectAllCompaniesMap,
  selectClientsMap,
} from 'src/store/clients/types';
import { zIndex } from 'src/theme/zindex';

export type RecipientType = 'client' | 'company' | 'internalUser';

export const selectRecipientComboData = createSelector(
  [
    (state: RootState) => state.clients.activeClients,
    selectAllCompaniesMap,
    (_, recipientTypes: RecipientType[]) => recipientTypes,
    (_, _r, excludedRecipients: { clientId: string; companyId: string }[]) =>
      excludedRecipients,
    (_, _r, _r2, isCompaniesEnabled: boolean) => isCompaniesEnabled,
  ],
  (
    clients,
    companiesMap,
    recipientTypes,
    excludedRecipients,
    isCompaniesEnabled,
  ) => {
    const clientOptions: UserCompanySelectorOption[] = [];
    const companyOptions: UserCompanySelectorOption[] = [];

    if (recipientTypes.includes('client')) {
      clients
        .sort((a, b) => b.createdDate.localeCompare(a.createdDate))
        .forEach((c) => {
          c.fields.companyIds?.forEach((companyId) => {
            const company = companiesMap.get(companyId);
            if (company) {
              if (
                excludedRecipients.some(
                  (r) => r.clientId === c.id && r.companyId === company.id,
                )
              ) {
                return;
              }
              clientOptions.push({
                value: c.id,
                label: `${c.fields.givenName} ${c.fields.familyName}`,
                companyId: company.id,
                type: 'client',
                avatarSrc: c.fields.avatarImageUrl,
                avatarFallbackColor: c.fields.fallbackColor,
              });
            }
          });
        });

      if (isCompaniesEnabled) {
        Array.from(companiesMap.values())
          .sort((a, b) => b.createdDate.localeCompare(a.createdDate))
          .forEach((company) => {
            if (
              company.fields.isPlaceholder ||
              company.entityStatus === 'ARCHIVED'
            ) {
              return;
            }

            companyOptions.push({
              value: company.id,
              label: company.fields.name,
              type: 'company',
              companyId: company.id,
              avatarSrc: company.fields.avatarImageURL,
              avatarFallbackColor: company.fields.fallbackColor,
            });
          });
      }
    }

    const internalUserOptions: UserCompanySelectorOption[] | undefined =
      undefined;

    if (recipientTypes.includes('internalUser')) {
      // TODO: Add internal user options
    }
    return {
      clientOptions,
      companyOptions,
      internalUserOptions,
    };
  },
);

/**
 * This selector is used to get the controlled value for the recipient selector.
 * This is needed in cases where the selected values is determined by the parent component
 * such as in ShareForms when submitting Select All Clients
 */
export const selectRecipientValues = createSelector(
  [
    selectClientsMap,
    selectAllCompaniesMap,
    (
      _,
      selectedRecipients: {
        clientId: string;
        companyId: string;
        type: RecipientType;
      }[],
    ) => selectedRecipients,
  ],
  (clients, companiesMap, selectedRecipients) => {
    const recipientInput: UserCompanySelectorOption[] = [];
    selectedRecipients.forEach((r) => {
      // Handle client selection (with or without company)
      if (r.type === 'client' && r.clientId) {
        const client = clients.get(r.clientId);
        if (client) {
          recipientInput.push({
            value: r.clientId,
            label: `${client.fields.givenName} ${client.fields.familyName}`,
            companyId:
              r.companyId ||
              (client.fields.companyIds?.length === 1
                ? client.fields.companyIds[0]
                : undefined),
            type: 'client',
            avatarSrc: client.fields.avatarImageUrl,
            avatarFallbackColor: client.fields.fallbackColor,
          });
        }
      }
      // Handle company-only selection
      else if (r.type === 'company' && r.companyId && !r.clientId) {
        const company = companiesMap.get(r.companyId);
        if (company) {
          recipientInput.push({
            value: r.companyId,
            label: company.fields.name,
            companyId: r.companyId,
            type: 'company',
            avatarSrc: company.fields.avatarImageURL,
            avatarFallbackColor: company.fields.fallbackColor,
          });
        }
      }
    });
    return recipientInput.length > 0 ? recipientInput : undefined;
  },
);

export const RecipientSelector = ({
  label,
  recipientTypes,
  onRecipientSelected,
  name,
  ignoreFilterClientsByCompany = false,
  excludedRecipients = [],
  placeholder = '',
  selectedRecipients = [],
  error,
  helperText,
  limitSelectedOptions,
  isDisabled = false,
  onBlur,
  openOnFocus = false,
  autoFocus = false,
}: {
  recipientTypes: RecipientType[];
  onRecipientSelected: React.ComponentProps<
    typeof UserCompanySelector
  >['onChange'];
  name: string;
  ignoreFilterClientsByCompany?: boolean;
  excludedRecipients?: { clientId: string; companyId: string }[];
  placeholder?: string;
  label?: string;
  selectedRecipients?: {
    clientId: string;
    companyId: string;
    type: RecipientType;
  }[];
  error?: boolean;
  helperText?: string;
  limitSelectedOptions?: number;
  isDisabled?: boolean;
  onBlur?: React.FocusEventHandler<HTMLDivElement>;
  openOnFocus?: boolean;
  autoFocus?: boolean;
}) => {
  const { data: config } = useGetPortalConfigQuery();
  const { t } = useTranslation();
  const isCompaniesEnabled = !config?.structFields?.disableCompanies;
  const { clientOptions, companyOptions, internalUserOptions } = useAppSelector(
    (state) =>
      selectRecipientComboData(
        state,
        recipientTypes,
        excludedRecipients,
        isCompaniesEnabled,
      ),
  );

  const selectedValues = useAppSelector((state) =>
    selectRecipientValues(state, selectedRecipients),
  );

  const isGrouped = recipientTypes.length > 1;

  return (
    <div>
      {label && <Heading size="sm">{label}</Heading>}
      <UserCompanySelector
        autoFocus={autoFocus}
        customLabels={{
          individualTerm: t(($) => $.client, { count: 1 }),
          individualTermPlural: t(($) => $.client, { count: 2 }),
          groupTerm: t(($) => $.company, { count: 1 }),
          groupTermPlural: t(($) => $.company, { count: 2 }),
        }}
        isDisabled={isDisabled}
        grouped={isGrouped}
        ignoreFilterClientsByCompany={ignoreFilterClientsByCompany}
        ignoreCompanies={!recipientTypes.includes('company')}
        clientUsers={clientOptions}
        companies={companyOptions}
        limitSelectedOptions={limitSelectedOptions}
        internalUsers={internalUserOptions}
        placeholder={placeholder}
        name={name}
        onChange={onRecipientSelected}
        onBlur={onBlur}
        openMenuOnFocus={openOnFocus}
        // this is needed for the menu to be inserted at the body in the html tree to avoid z-index issues.
        // E.g. if this selector is used in a modal, the menu would otherwise be inserted in the modal's
        // root which has a lower z-index than the modal itself and thus the menu would expand the height
        // of the modal instead of opening over it.
        menuPortalTarget={document.body}
        styles={{
          placeholder: (base) => ({
            ...base,
            color: '#9B9FA3',
            fontSize: 14,
          }),
          menuPortal: (base) => ({ ...base, zIndex: zIndex.modal }),
          control: (base) => ({
            ...base,
            // temporary fix to show the error state on the input
            // this should be done in the design system
            ...(error && {
              border: '1px solid #991A00',
              borderRadius: 4,
            }),
          }),
        }}
        value={selectedValues}
      />
      {error && (
        <ErrorMessage
          message={helperText || ''}
          className="text-sm flex justify-end"
        />
      )}
    </div>
  );
};
